/**
 * DayDetailPanel — Displays tasks due and events for the selected day.
 *
 * Reads selectedDate from useCalendarStore, queries events for that day
 * and tasks due on that day from useTaskStore. Sorts: all-day events first,
 * then by startTime/dueTime. Shows event title, time, location. Shows task
 * title with priority badge and completion status.
 *
 * @see Requirements 5.4, 5.5
 */

import { useMemo } from 'react';
import { useCalendarStore } from '../../stores/calendarStore';
import { useTaskStore } from '../../stores/taskStore';
import type { CalendarEvent } from '../../types/calendar';
import type { Task } from '../../types/tasks';
import { Badge } from '../ui/Badge';

// ─── Types ───────────────────────────────────────────────────────────────────

/** A unified item representing either an event or a task in the day detail. */
interface DayItem {
  type: 'event' | 'task';
  id: string;
  title: string;
  /** Sort key: all-day items get '00:00', timed items get HH:mm. */
  sortTime: string;
  isAllDay: boolean;
  event?: CalendarEvent;
  task?: Task;
}

// ─── Priority Color Map ──────────────────────────────────────────────────────

const PRIORITY_COLORS: Record<string, string> = {
  high: 'var(--color-priority-high)',
  medium: 'var(--color-priority-medium)',
  low: 'var(--color-priority-low)',
  none: 'var(--color-text-secondary)',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Extract time (HH:mm) from an ISO datetime string.
 * Returns '00:00' for all-day events or if no time part is found.
 */
function extractTime(isoDatetime: string): string {
  if (isoDatetime.includes('T')) {
    const timePart = isoDatetime.split('T')[1];
    if (timePart) {
      return timePart.substring(0, 5);
    }
  }
  return '00:00';
}

/**
 * Format a time string (HH:mm) for display (e.g., "9:30 AM").
 */
function formatTime(time: string): string {
  const [hoursStr, minutesStr] = time.split(':');
  const hours = parseInt(hoursStr ?? '0', 10);
  const minutes = minutesStr ?? '00';
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  return `${displayHour}:${minutes} ${ampm}`;
}

/**
 * Format a date string (YYYY-MM-DD) to a display label.
 */
function formatDateLabel(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

// ─── Component ───────────────────────────────────────────────────────────────

export function DayDetailPanel() {
  const { selectedDate, events } = useCalendarStore();
  const { tasks } = useTaskStore();

  // Filter events for the selected day
  const dayEvents = useMemo(() => {
    return events.filter((event) => {
      const eventDate = event.startDate.split('T')[0] ?? event.startDate;
      return eventDate === selectedDate;
    });
  }, [events, selectedDate]);

  // Filter tasks due on the selected day (include both completed and incomplete)
  const dayTasks = useMemo(() => {
    return tasks.filter(
      (task) => task.dueDate === selectedDate && !task.deleted
    );
  }, [tasks, selectedDate]);

  // Build unified list and sort: all-day first, then by time
  const sortedItems = useMemo(() => {
    const items: DayItem[] = [];

    // Add events
    for (const event of dayEvents) {
      items.push({
        type: 'event',
        id: event.eventId,
        title: event.title,
        sortTime: event.isAllDay ? '00:00' : extractTime(event.startDate),
        isAllDay: event.isAllDay,
        event,
      });
    }

    // Add tasks
    for (const task of dayTasks) {
      items.push({
        type: 'task',
        id: task.taskId,
        title: task.title,
        sortTime: task.dueTime ?? '23:59', // tasks without time go to end
        isAllDay: !task.dueTime,
        task,
      });
    }

    // Sort: all-day items first, then by sortTime ascending
    items.sort((a, b) => {
      // All-day items come first
      if (a.isAllDay && !b.isAllDay) return -1;
      if (!a.isAllDay && b.isAllDay) return 1;
      // Then sort by time
      return a.sortTime.localeCompare(b.sortTime);
    });

    return items;
  }, [dayEvents, dayTasks]);

  const dateLabel = formatDateLabel(selectedDate);
  const isEmpty = sortedItems.length === 0;

  return (
    <div
      className="p-4 bg-bg-card rounded-xl shadow-card"
      role="region"
      aria-label={`Schedule for ${dateLabel}`}
    >
      {/* Header */}
      <h3 className="text-base font-semibold text-text-heading mb-3">
        {dateLabel}
      </h3>

      {/* Empty state */}
      {isEmpty && (
        <div className="py-6 text-center">
          <p className="text-sm text-text-secondary">
            No events or tasks for this day.
          </p>
        </div>
      )}

      {/* Items list */}
      {!isEmpty && (
        <ul className="space-y-2" aria-label="Day schedule">
          {sortedItems.map((item) => (
            <li key={`${item.type}-${item.id}`}>
              {item.type === 'event' && item.event && (
                <EventRow event={item.event} />
              )}
              {item.type === 'task' && item.task && (
                <TaskRow task={item.task} />
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Summary */}
      {!isEmpty && (
        <div className="mt-3 pt-3 border-t border-border-subtle flex items-center gap-3 text-xs text-text-secondary">
          <span>{dayEvents.length} event{dayEvents.length !== 1 ? 's' : ''}</span>
          <span>{dayTasks.length} task{dayTasks.length !== 1 ? 's' : ''}</span>
        </div>
      )}
    </div>
  );
}

// ─── Sub-Components ──────────────────────────────────────────────────────────

function EventRow({ event }: { event: CalendarEvent }) {
  const timeDisplay = event.isAllDay
    ? 'All day'
    : `${formatTime(extractTime(event.startDate))} – ${formatTime(extractTime(event.endDate))}`;

  const sourceColor = event.color || 'var(--color-secondary)'; // lavender default

  return (
    <div className="flex items-start gap-3 p-2 rounded-lg hover:bg-hover-bg transition-colors duration-200">
      {/* Color indicator */}
      <span
        className="mt-1 w-2.5 h-2.5 rounded-full shrink-0"
        style={{ backgroundColor: sourceColor }}
        aria-hidden="true"
      />

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-text-primary truncate">
          {event.title}
        </p>
        <p className="text-xs text-text-secondary mt-0.5">
          {timeDisplay}
        </p>
        {event.location && (
          <p className="text-xs text-text-secondary mt-0.5 flex items-center gap-1">
            <LocationIcon />
            <span className="truncate">{event.location}</span>
          </p>
        )}
      </div>

      {/* Source badge */}
      {event.source !== 'local' && (
        <Badge
          text={event.source}
          size="sm"
          variant="outlined"
          className="shrink-0 capitalize"
        />
      )}
    </div>
  );
}

function TaskRow({ task }: { task: Task }) {
  const timeDisplay = task.dueTime ? formatTime(task.dueTime) : null;
  const priorityColor = PRIORITY_COLORS[task.priority] ?? PRIORITY_COLORS.none;

  return (
    <div className="flex items-start gap-3 p-2 rounded-lg hover:bg-hover-bg transition-colors duration-200">
      {/* Completion checkbox indicator */}
      <span
        className={[
          'mt-0.5 w-4 h-4 rounded border-2 shrink-0 flex items-center justify-center',
          task.isCompleted
            ? 'bg-primary border-primary'
            : 'border-border',
        ].join(' ')}
        aria-hidden="true"
      >
        {task.isCompleted && (
          <svg
            width="10"
            height="10"
            viewBox="0 0 16 16"
            fill="none"
            className="text-white"
          >
            <path
              d="M4 8L7 11L12 5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </span>

      <div className="flex-1 min-w-0">
        <p
          className={[
            'text-sm font-medium truncate',
            task.isCompleted
              ? 'line-through text-text-secondary'
              : 'text-text-primary',
          ].join(' ')}
        >
          {task.title}
        </p>
        {timeDisplay && (
          <p className="text-xs text-text-secondary mt-0.5">
            Due {timeDisplay}
          </p>
        )}
      </div>

      {/* Priority badge */}
      {task.priority !== 'none' && (
        <Badge
          text={task.priority}
          color={priorityColor}
          size="sm"
          variant="filled"
          className="shrink-0 capitalize"
        />
      )}
    </div>
  );
}

// ─── Icons ───────────────────────────────────────────────────────────────────

function LocationIcon() {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="shrink-0"
    >
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}
